import React, { useEffect, useState, useCallback, useRef } from "react";
import { Smartphone, Loader2, AlertTriangle, CheckCircle2, ShieldCheck, Trash2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SuperAdminLoginMobileCard — lets the active Super Admin set / update / clear
 * their own mobile-OTP login number. Mirrors the staff login_mobile flow in
 * Team Hub but targets the super-admin-specific endpoints. Admin role only.
 */
const SuperAdminLoginMobileCard = ({ authAxios, role }) => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  // Only seed the input from the server on the very first load — later refetches
  // (authAxios identity can change across Admin.js re-renders) must not clobber
  // what the user is typing.
  const draftSeeded = useRef(false);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/super-admin/profile`);
      setProfile(r.data);
      if (!draftSeeded.current) {
        setDraft(r.data?.login_mobile || "");
        draftSeeded.current = true;
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load profile");
    }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { if (role === "admin") fetchProfile(); }, [role, fetchProfile]);

  if (role !== "admin") return null;

  const save = async (clear = false) => {
    setErr(""); setOk("");
    const value = clear ? "" : (draft ?? "").trim();
    if (!clear && !value) { setErr("Enter a mobile number, or use the trash icon to remove it."); return; }
    setSaving(true);
    try {
      const r = await authAxios.put(`${API_URL}/api/admin/super-admin/login-mobile`, { login_mobile: value });
      const savedMobile = r.data?.login_mobile || null;
      setOk(savedMobile ? "Login mobile saved. Verify it by logging in with an OTP." : "Login mobile removed.");
      setProfile((p) => ({ ...(p || {}), login_mobile: savedMobile, login_mobile_verified: r.data?.login_mobile_verified ?? false }));
      setDraft(savedMobile || "");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to save login mobile");
    }
    setSaving(false);
  };

  const verified = profile?.login_mobile_verified;
  const hasMobile = !!profile?.login_mobile;

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5 shadow-sm" data-testid="super-admin-login-mobile-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-600 text-white">
          <Smartphone size={18} />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-bold text-[#1E1B4B] font-['Outfit']">Super Admin OTP Login Mobile</h3>
          <p className="text-xs text-[#64748B] mt-0.5">
            Set the mobile you'll use to sign in with a one-time code. OTP login skips the 2FA
            prompt, so keep this number secure. Must be unique across all staff.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 size={20} className="animate-spin text-sky-500" /></div>
      ) : (
        <>
          <div className="mb-3 flex items-center gap-2">
            <span className="text-xs text-[#64748B]">Current status:</span>
            {hasMobile ? (
              verified ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[11px] font-semibold" data-testid="sa-login-mobile-status">
                  <ShieldCheck size={11} /> Verified
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-[11px] font-semibold" data-testid="sa-login-mobile-status">
                  <AlertTriangle size={11} /> Pending verification
                </span>
              )
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 text-gray-500 text-[11px] font-semibold" data-testid="sa-login-mobile-status">
                Not set
              </span>
            )}
          </div>

          <div className="flex gap-2 max-w-md">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="+91XXXXXXXXXX"
              className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-xl focus:border-sky-400 outline-none"
              data-testid="sa-login-mobile-input"
            />
            <button
              onClick={() => save(false)}
              disabled={saving}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-sky-600 to-indigo-600 text-white text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
              data-testid="sa-login-mobile-save-btn"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Save
            </button>
            {hasMobile && (
              <button
                onClick={() => save(true)}
                disabled={saving}
                className="px-3 py-2 rounded-xl bg-red-50 text-red-600 text-sm font-medium hover:bg-red-100 disabled:opacity-50 flex items-center gap-1"
                data-testid="sa-login-mobile-clear-btn"
                title="Remove login mobile"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>

          {err && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-700 flex items-center gap-2" data-testid="sa-login-mobile-error">
              <AlertTriangle size={14} /> {err}
            </div>
          )}
          {ok && (
            <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 text-xs text-emerald-700 flex items-center gap-2" data-testid="sa-login-mobile-success">
              <CheckCircle2 size={14} /> {ok}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SuperAdminLoginMobileCard;
